import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Crown, Loader2, Flame } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { fetchPlans, createCheckoutSession, formatApiError } from "../../lib/studioApi";

// Plan-card content — display copy lives here so the Stripe price IDs +
// amounts stay server-side, but the card descriptions/features are easy to
// tweak without redeploying the API.
const PLAN_COPY = {
  studio_membership: {
    label: "Studio Membership",
    accent: "yellow",
    icon: Sparkles,
    description: "Everything you need to grow as an artist.",
    features: [
      "Access to all Perks",
      "Access to all Lessons",
      "Access to Sample Packs",
      "Access to Content Library",
      "Access to Resources",
      "Access to future member content",
      "Access to Studio community features",
    ],
    cta: "Join now",
  },
  studio_plus: {
    label: "Studio Plus",
    accent: "gold",
    icon: Crown,
    description: "Studio Membership plus 1-on-1 mentoring.",
    features: [
      "Everything in Studio Membership",
      "One 60-minute 1-on-1 call with Paul per month",
      "Career development support",
      "Release strategy guidance",
      "Artist feedback sessions",
      "Monthly accountability check-in",
    ],
    cta: "Join now",
    badge: "Most popular",
  },
  private_studio_production: {
    label: "Private Studio Production",
    accent: "muted",
    icon: Sparkles,
    description: "Hands-on production support and collaboration.",
    features: [
      "Song development",
      "Production",
      "Arrangement",
      "Mixing",
      "Release-ready guidance",
      "Private Studio access",
    ],
    cta: "Book a call",
    customPrice: "Custom",
  },
};

const TIERS_ORDER = ["studio_membership", "studio_plus", "private_studio_production"];

function priceLine(plans, tier, cycle) {
  if (tier === "private_studio_production") return "Custom";
  const key = `${tier}_${cycle}`;
  const p = plans.find((pl) => pl.key === key);
  if (!p) return "—";
  const per = cycle === "yearly" ? "/year" : "/month";
  return `$${Number(p.amount).toFixed(0)}${per}`;
}

function monthlyEquivalent(plans, tier) {
  // Show the "equivalent /mo" line on yearly so users see the savings clearly.
  const y = plans.find((pl) => pl.key === `${tier}_yearly`);
  if (!y) return null;
  return `~$${(Number(y.amount) / 12).toFixed(0)}/mo billed yearly`;
}

export default function PricingSection() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly"); // monthly | yearly
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    fetchPlans().then(setPlans).catch(() => toast.error("Could not load plans"));
  }, []);

  const handleJoin = async (tier) => {
    if (tier === "private_studio_production") {
      navigate("/inquiry");
      return;
    }
    const planKey = `${tier}_${cycle}`;
    setBusyKey(planKey);
    try {
      const { url } = await createCheckoutSession(planKey, window.location.origin);
      if (!url) {
        toast.error("Could not start checkout");
        return;
      }
      window.location.href = url;
    } catch (err) {
      toast.error(
        formatApiError(
          err,
          "Unable to start checkout right now. Please try again or contact support.",
        ),
      );
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <section
      data-testid="studio-pricing-section"
      className="w-full max-w-6xl mx-auto px-4 md:px-6 pb-20 pt-4"
    >
      {/* Heading */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-paul-yellow mb-3">
          <Sparkles className="w-3.5 h-3.5" />
          Join the Studio
        </div>
        <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
          Become a Studio member.
        </h2>
        <p className="text-white/55 text-base md:text-lg mt-3 max-w-2xl mx-auto">
          Get access to exclusive resources, lessons, content, support, and opportunities to grow as an artist.
        </p>
      </div>

      {/* Billing toggle */}
      <div className="flex flex-col items-center mb-8 gap-3">
        <div
          data-testid="billing-toggle"
          className="inline-flex bg-[#10162a] border border-white/10 rounded-full p-1 relative"
        >
          <button
            type="button"
            data-testid="billing-toggle-monthly"
            onClick={() => setCycle("monthly")}
            className={`relative z-10 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
              cycle === "monthly" ? "bg-paul-yellow text-navy" : "text-white/70 hover:text-white"
            }`}
            style={cycle === "monthly" ? { color: "#0b1535" } : undefined}
          >
            Monthly
          </button>
          <button
            type="button"
            data-testid="billing-toggle-yearly"
            onClick={() => setCycle("yearly")}
            className={`relative z-10 px-5 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${
              cycle === "yearly" ? "bg-paul-yellow text-navy" : "text-white/70 hover:text-white"
            }`}
            style={cycle === "yearly" ? { color: "#0b1535" } : undefined}
          >
            Yearly
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                cycle === "yearly" ? "bg-navy/15 text-navy" : "bg-paul-yellow/15 text-paul-yellow"
              }`}
              style={cycle === "yearly" ? { color: "#0b1535" } : undefined}
            >
              Save 20%
            </span>
          </button>
        </div>
        {cycle === "yearly" && (
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-paul-yellow">
            <Flame className="w-3.5 h-3.5" />
            Save 20% with annual billing
          </div>
        )}
      </div>

      {/* Pricing grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {TIERS_ORDER.map((tier) => {
          const copy = PLAN_COPY[tier];
          const isPlus = tier === "studio_plus";
          const isCustom = tier === "private_studio_production";
          const Icon = copy.icon;
          const planKey = `${tier}_${cycle}`;
          return (
            <article
              key={tier}
              data-testid={`pricing-card-${tier}`}
              className={`relative rounded-2xl border p-6 md:p-7 flex flex-col bg-[#0f1322] transition-all ${
                isPlus
                  ? "border-paul-yellow/40 shadow-[0_18px_60px_rgba(245,193,75,0.18)] md:scale-[1.02] z-10"
                  : "border-white/10 hover:border-white/20"
              }`}
            >
              {copy.badge && (
                <span
                  data-testid={`pricing-card-badge-${tier}`}
                  className="absolute -top-3 left-1/2 -translate-x-1/2 bg-paul-yellow text-navy text-[10px] uppercase tracking-[0.18em] font-extrabold px-3 py-1 rounded-full"
                  style={{ color: "#0b1535" }}
                >
                  {copy.badge}
                </span>
              )}

              <div className="flex items-center gap-2 mb-3">
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    isPlus ? "bg-paul-yellow text-navy" : "bg-white/10 text-paul-yellow"
                  }`}
                  style={isPlus ? { color: "#0b1535" } : undefined}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50">
                  {copy.label}
                </div>
              </div>

              <h3 className="text-2xl font-extrabold text-white leading-tight">
                {copy.label}
              </h3>
              <p className="text-sm text-white/55 mt-1 mb-5 leading-relaxed">
                {copy.description}
              </p>

              {/* Price */}
              <div className="mb-5">
                {isCustom ? (
                  <div className="text-3xl md:text-4xl font-extrabold text-white">Custom</div>
                ) : (
                  <>
                    <div
                      data-testid={`pricing-card-amount-${tier}`}
                      className="text-3xl md:text-4xl font-extrabold text-white"
                    >
                      {priceLine(plans, tier, cycle)}
                    </div>
                    {cycle === "yearly" && (
                      <div className="text-[12px] text-paul-yellow font-semibold mt-1">
                        {monthlyEquivalent(plans, tier)}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-2 text-sm text-white/80 mb-7">
                {copy.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-paul-yellow flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                type="button"
                data-testid={`pricing-card-cta-${tier}`}
                onClick={() => handleJoin(tier)}
                disabled={busyKey === planKey}
                className={`mt-auto w-full h-12 font-extrabold text-base ${
                  isPlus
                    ? "bg-paul-yellow hover:brightness-105 text-navy"
                    : isCustom
                      ? "bg-white text-navy hover:bg-white/90"
                      : "bg-paul-yellow hover:brightness-105 text-navy"
                }`}
                style={isPlus || !isCustom ? { color: "#0b1535" } : undefined}
              >
                {busyKey === planKey ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  copy.cta.toUpperCase()
                )}
              </Button>
            </article>
          );
        })}
      </div>

      <p className="text-center text-[11px] text-white/30 mt-10">
        Subscriptions are auto-renewing. Cancel anytime from your profile — access continues until the end of the billing period.
      </p>
    </section>
  );
}
